/**
 * Cookie consent state.
 *
 * The decision lives in a cookie, but it has to be readable by any component
 * that loads a third party (Intercom today, analytics later), so it is mirrored
 * into redux rather than each of them parsing `document.cookie` for themselves.
 *
 * `consent` is null until the banner has read the cookie. That is deliberate
 * and is not the same as "declined": consumers use hasConsent(), which fails
 * closed, so nothing loads in the window before the answer is known.
 *
 * `reopenRequested` is how the footer's "Cookie settings" link brings the
 * banner back after someone has already answered. Law 25 expects withdrawing
 * consent to be as easy as giving it, and until now there was no way back at
 * all: once the cookie was set the banner never returned.
 */

import { readConsent, writeConsent } from '../util/cookieConsent';
import { consentAPI } from '../util/api';

// ================ Action types ================ //

export const CONSENT_LOADED = 'app/cookieConsent/CONSENT_LOADED';
export const CONSENT_SAVED = 'app/cookieConsent/CONSENT_SAVED';
export const CONSENT_REOPEN_REQUESTED = 'app/cookieConsent/CONSENT_REOPEN_REQUESTED';
export const CONSENT_REOPEN_HANDLED = 'app/cookieConsent/CONSENT_REOPEN_HANDLED';

// ================ Reducer ================ //

const initialState = {
  // null = not yet read. Never treat as "declined" without checking `loaded`.
  consent: null,
  loaded: false,
  reopenRequested: false,
};

export default function cookieConsentReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CONSENT_LOADED:
      return { ...state, consent: payload, loaded: true };
    case CONSENT_SAVED:
      return { ...state, consent: payload, loaded: true, reopenRequested: false };
    case CONSENT_REOPEN_REQUESTED:
      return { ...state, reopenRequested: true };
    case CONSENT_REOPEN_HANDLED:
      return { ...state, reopenRequested: false };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const consentLoaded = consent => ({ type: CONSENT_LOADED, payload: consent });
export const consentSaved = consent => ({ type: CONSENT_SAVED, payload: consent });
export const requestReopenConsent = () => ({ type: CONSENT_REOPEN_REQUESTED });
export const reopenConsentHandled = () => ({ type: CONSENT_REOPEN_HANDLED });

// ================ Thunks ================ //

/** Read the stored decision into state. Dispatched by the banner on mount. */
export const loadConsent = () => dispatch => {
  const consent = readConsent();
  dispatch(consentLoaded(consent));
  return consent;
};

/**
 * Record a decision.
 *
 * The cookie is written first and the state updated immediately, because the
 * visitor's choice must take effect whether or not our own logging endpoint is
 * reachable. The server-side record is the audit trail, not the mechanism.
 */
export const saveConsent = preferences => async dispatch => {
  const record = writeConsent(preferences);
  dispatch(consentSaved(record));

  try {
    await consentAPI.log({
      consentId: record.consentId,
      version: record.version,
      timestamp: record.timestamp,
      categories: {
        essential: record.essential,
        functional: record.functional,
        statistics: record.statistics,
      },
      // The wording actually shown, so the record can be read back without
      // guessing which version of the copy was on screen at the time.
      locale: typeof document !== 'undefined' ? document.documentElement.lang || null : null,
    });
  } catch (e) {
    // Never let an audit write undo the visitor's choice.
    console.error('[cookieConsent] consent log failed:', e?.message || e);
  }

  return record;
};
